import { useEffect, useRef, useState } from 'react';
import { Box } from '@mantine/core';
import { Outlet, useLocation, useNavigationType } from 'react-router-dom';
import { BetaHeader } from './beta-header';
import { BetaFooter } from './beta-footer';
import { BetaMobileTabs } from './beta-mobile-tabs';
import { BetaErrorBoundary } from '../components/beta-error-boundary';
import { ProfessorElm } from '../components/professor-elm';

// Global offline banner: a thin, calm bar shown for every beta page while the
// browser reports no connection, so "nothing is loading" is never a mystery.
function OfflineBanner() {
  const [online, setOnline] = useState(
    () => (typeof navigator === 'undefined' ? true : navigator.onLine),
  );
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  if (online) return null;
  return (
    <div className="beta-offline-banner" role="status">
      You appear to be offline — data may not load until you reconnect.
    </div>
  );
}

// Scroll offset per history entry, so Back returns to where the list was.
const scrollByKey = new Map<string, number>();

export function BetaLayout() {
  const location = useLocation();
  const navType = useNavigationType();
  const keyRef = useRef(location.key);
  const pathRef = useRef(location.pathname);

  useEffect(() => {
    const onScroll = () => scrollByKey.set(keyRef.current, window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Fresh error boundary per path; a query change (e.g. `/search?q=a`)
  // only clears a caught error. Keying on the query too remounted the page on
  // every debounced URL sync, dropping focus from whatever input was typing.
  // Scroll to top on path changes (same-page query tweaks keep position);
  // Back/Forward restores the saved offset once the page is tall enough.
  useEffect(() => {
    keyRef.current = location.key;
    const pathChanged = pathRef.current !== location.pathname;
    pathRef.current = location.pathname;
    if (navType === 'POP') {
      const target = scrollByKey.get(location.key) ?? 0;
      if (!target) {
        if (pathChanged) window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
        return;
      }
      const start = performance.now();
      let raf = 0;
      const tryRestore = () => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        if (max >= target || performance.now() - start > 1000) {
          window.scrollTo({ top: target, behavior: 'instant' as ScrollBehavior });
          return;
        }
        raf = requestAnimationFrame(tryRestore);
      };
      tryRestore();
      return () => cancelAnimationFrame(raf);
    }
    if (pathChanged) window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [location.key, location.pathname, navType]);

  return (
    <Box className="beta-frame">
      <a href="#beta-main" className="beta-skip-link">
        Skip to content
      </a>
      <BetaHeader />
      <OfflineBanner />
      <Box component="main" id="beta-main" className="beta-main" tabIndex={-1}>
        <div className="beta-container">
          <BetaErrorBoundary key={location.pathname} resetKey={location.search}>
            <div className="beta-page-enter">
              <Outlet />
            </div>
          </BetaErrorBoundary>
        </div>
      </Box>
      <BetaFooter />
      <BetaMobileTabs />
      <ProfessorElm />
    </Box>
  );
}
